/**
 * The checks the browser runs on a video file BEFORE the upload
 * (spec 2026-10-23 §5: "checks in the browser (type, size, length, shape)").
 *
 * These are a convenience: they save a 2 GB upload that the server would
 * refuse. They are NOT the control. The server converts the file and checks
 * the RESULT again, so when the browser cannot open a file (`meta === null`,
 * e.g. ProRes in Chrome) only the file checks run here and the server decides
 * the rest. Every limit comes from the shared contract.
 *
 * Each refusal says what is wrong and how to fix it, in short sentences.
 * Pure (no DOM) except `readVideoMeta`, whose DOM access is injectable.
 */
import {
  VIDEO_MAX_ASPECT,
  VIDEO_MAX_DURATION_MS,
  VIDEO_MIN_ASPECT,
  VIDEO_RECOMMENDED_MIN_SHORT_SIDE,
  VIDEO_UPLOAD_EXTENSIONS,
  VIDEO_UPLOAD_MAX_BYTES,
} from './contract';

export interface VideoMeta {
  durationMs: number;
  width: number;
  height: number;
}

export interface FileCheck {
  errors: string[];
  warnings: string[];
}

type Extension = (typeof VIDEO_UPLOAD_EXTENSIONS)[number];

const EXTENSION_NAMES: Record<Extension, string> = {
  mp4: 'MP4',
  mov: 'MOV',
  m4v: 'M4V',
  webm: 'WebM',
  mkv: 'MKV',
};

/** The display order of the brief: MP4, MOV, M4V, WebM, MKV. A type added to the contract goes last. */
const DISPLAY_ORDER: readonly string[] = ['mp4', 'mov', 'm4v', 'webm', 'mkv'];
const rank = (e: string) => (DISPLAY_ORDER.includes(e) ? DISPLAY_ORDER.indexOf(e) : DISPLAY_ORDER.length);
const TYPE_NAMES = [...VIDEO_UPLOAD_EXTENSIONS].sort((a, b) => rank(a) - rank(b)).map((e) => EXTENSION_NAMES[e]);

const BYTES_PER_GB = 1024 * 1024 * 1024;
const MAX_GB = VIDEO_UPLOAD_MAX_BYTES / BYTES_PER_GB;
const MAX_SECONDS = VIDEO_MAX_DURATION_MS / 1000;

/** "2:1" for 2, "1:2" for 0.5. The limits are whole-number ratios. */
function ratioWords(aspect: number): string {
  return aspect >= 1 ? `${aspect}:1` : `1:${1 / aspect}`;
}
const WIDEST = ratioWords(VIDEO_MAX_ASPECT);
const TALLEST = ratioWords(VIDEO_MIN_ASPECT);

/** Printed under the upload button, exactly as the brief words it. */
export const ACCEPTED_FILES_TEXT = `${TYPE_NAMES.join(', ')} — up to ${MAX_GB} GB — any shape from ${WIDEST} to ${TALLEST}`;

/** Content rules no software can fix (spec §7). Printed under the button. */
export const UPLOAD_RULES: readonly string[] = [
  'The video starts without sound. Put the words inside the video.',
  'Skip appears at 5 s — the first 5 s must carry the message.',
  'No more than 3 flashes in any second.',
];

/** For <input type="file" accept>: the extensions, so the picker shows the right files. */
export const ACCEPT_ATTRIBUTE = VIDEO_UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(',');

/**
 * The longest file name the server accepts: `file_name` in the upload schema of
 * backend/src/routes/admin-app-experience.ts is Joi `.trim().max(255)`. That
 * limit is not in the shared contract, so it is written here and its source is
 * pinned by tests/unit/admin/app-experience-wiring-lint.test.ts (clause 7).
 * Without this check a longer name came back as raw schema text.
 */
export const FILE_NAME_MAX_CHARS = 255;

const TYPES_SENTENCE = `${TYPE_NAMES.slice(0, -1).join(', ')} or ${TYPE_NAMES[TYPE_NAMES.length - 1]}`;

function extensionOf(name: string): string | null {
  const m = /\.([A-Za-z0-9]+)$/.exec(name);
  return m ? m[1].toLowerCase() : null;
}

/** 60 001 ms → "60.1"; 75 000 ms → "75". Rounded UP, so a video over the limit never reads as on it. */
function seconds(ms: number): string {
  const tenths = Math.ceil(ms / 100) / 10;
  return Number.isInteger(tenths) ? String(tenths) : tenths.toFixed(1);
}

function gigabytes(bytes: number): string {
  return (Math.ceil((bytes / BYTES_PER_GB) * 10) / 10).toFixed(1);
}

export function checkVideoFile(file: Pick<File, 'name' | 'size'>, meta: VideoMeta | null): FileCheck {
  const errors: string[] = [];
  const warnings: string[] = [];

  // The server trims the name before it checks it; do the same.
  const name = file.name.trim();
  if (name.length > FILE_NAME_MAX_CHARS) {
    errors.push(
      `The file name is too long (more than ${FILE_NAME_MAX_CHARS} characters). Rename the file to a shorter name and try again.`,
    );
  }
  const ext = extensionOf(name);
  if (ext === null) {
    errors.push(`The file has no file type. Use ${TYPES_SENTENCE}.`);
  } else if (!(VIDEO_UPLOAD_EXTENSIONS as readonly string[]).includes(ext)) {
    errors.push(`The file type .${ext} is not allowed. Use ${TYPES_SENTENCE}.`);
  }
  if (file.size === 0) {
    errors.push('This file is empty. Choose the video file again.');
  } else if (file.size > VIDEO_UPLOAD_MAX_BYTES) {
    errors.push(`This file is ${gigabytes(file.size)} GB. The limit is ${MAX_GB} GB. Export a smaller file.`);
  }

  if (meta) {
    if (meta.durationMs > VIDEO_MAX_DURATION_MS) {
      errors.push(`This video is ${seconds(meta.durationMs)} s long. The limit is ${MAX_SECONDS} s. Make the video shorter.`);
    }
    const aspect = meta.width / meta.height;
    if (aspect > VIDEO_MAX_ASPECT || aspect < VIDEO_MIN_ASPECT) {
      const side = aspect > VIDEO_MAX_ASPECT ? `wider than ${WIDEST}` : `taller than ${TALLEST}`;
      errors.push(
        `This video is ${meta.width} × ${meta.height}, which is ${side}. ` +
          `Use a shape from ${WIDEST} (wide) to ${TALLEST} (tall). Crop the video.`,
      );
    }
    const shortSide = Math.min(meta.width, meta.height);
    if (shortSide < VIDEO_RECOMMENDED_MIN_SHORT_SIDE) {
      warnings.push(
        `This video is small (${meta.width} × ${meta.height}). It can look blurry on a phone. ` +
          `Use at least ${VIDEO_RECOMMENDED_MIN_SHORT_SIDE} px on the short side (1080 px is best).`,
      );
    }
  }
  return { errors, warnings };
}

// ─── readVideoMeta ──────────────────────────────────────────────────────────

export interface ReadMetaDeps {
  createVideo: () => HTMLVideoElement;
  createObjectURL: (file: Blob) => string;
  revokeObjectURL: (url: string) => void;
  timeoutMs: number;
}

const browserDeps = (): ReadMetaDeps => ({
  createVideo: () => document.createElement('video'),
  createObjectURL: (f) => URL.createObjectURL(f),
  revokeObjectURL: (u) => URL.revokeObjectURL(u),
  timeoutMs: 5000,
});

/**
 * The length and picture size of a local file, read by the browser's own
 * decoder: an object URL → <video preload="metadata"> → `loadedmetadata`, or
 * `error`, or nothing within 5 s. Returns null whenever the browser cannot
 * tell (an unknown codec, an audio-only file, a stream with no length). The
 * object URL is revoked exactly once on every path.
 */
export function readVideoMeta(file: Blob, deps: ReadMetaDeps = browserDeps()): Promise<VideoMeta | null> {
  return new Promise((resolve) => {
    const video = deps.createVideo();
    const url = deps.createObjectURL(file);
    let settled = false;

    const finish = (result: VideoMeta | null) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.removeEventListener('loadedmetadata', onMeta);
      video.removeEventListener('error', onError);
      video.removeAttribute('src');
      video.load();
      deps.revokeObjectURL(url);
      resolve(result);
    };
    const onMeta = () => {
      const durationMs = Math.round(video.duration * 1000);
      const { videoWidth: width, videoHeight: height } = video;
      const known = Number.isFinite(durationMs) && durationMs > 0 && width > 0 && height > 0;
      finish(known ? { durationMs, width, height } : null);
    };
    const onError = () => finish(null);
    const timer = setTimeout(() => finish(null), deps.timeoutMs);

    video.preload = 'metadata';
    video.muted = true;
    video.addEventListener('loadedmetadata', onMeta);
    video.addEventListener('error', onError);
    video.src = url;
  });
}

// ─── words for a video that is already converted ───────────────────────────

/** "0:15" for 15 000 ms. */
export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/** "Square", "4:5", "9:16", "16:9" — or "1080 × 1350" for any other shape. */
export function shapeLabel(width: number, height: number): string {
  const known: [number, number, string][] = [
    [1, 1, 'Square'],
    [4, 5, '4:5'],
    [5, 4, '5:4'],
    [9, 16, '9:16'],
    [16, 9, '16:9'],
    [3, 4, '3:4'],
    [4, 3, '4:3'],
    [2, 3, '2:3'],
    [3, 2, '3:2'],
    [1, 2, '1:2'],
    [2, 1, '2:1'],
  ];
  const aspect = width / height;
  const hit = known.find(([w, h]) => Math.abs(aspect - w / h) < 0.01);
  return hit ? hit[2] : `${width} × ${height}`;
}
